import { startCase, get } from 'lodash/fp';
import { UserRole } from '@upstart/harmony/lib/constants';
import { UserType } from '@upstart/harmony/lib/legos/constants';

const getUserRequiredFields = ({ user, dealer }) => {
  const directLoginEnabled = get('featureConfig.crmDirectLogin.enabled')(
    dealer,
  );
  const requiredFields = ['firstName', 'lastName', 'roles'];

  if (!directLoginEnabled) {
    // Require user credentials
    requiredFields.push('email', 'username');

    // Require password for a new user
    if (!user.id) {
      requiredFields.push('password');
    }
  } else {
    requiredFields.push('crmLogin');
  }

  return requiredFields;
};

const getBaseProperties = ({ user, accessibleRoles }) => ({
  firstName: { type: 'string', title: 'First Name' },
  lastName: { type: 'string', title: 'Last Name' },
  email: { type: 'string', title: 'Email' },
  username: { type: 'string', title: 'Username' },
  phone: {
    type: ['string', 'null'],
    title: 'Phone',
    pattern: '^\\d{3}-\\d{3}-\\d{4}$',
  },
  ...(!user.id && { password: { type: 'string', title: 'Password' } }),
  roles: {
    type: 'array',
    title: 'Roles',
    items: {
      type: 'string',
      enum: accessibleRoles,
      enumNames: accessibleRoles.map(r => startCase(r)),
    },
    uniqueItems: true,
  },
});

export const getUserSchema = ({ user, dealer, accessibleRoles }) => ({
  title: 'User Specifications',
  type: 'object',
  required: getUserRequiredFields({ user, dealer }),
  properties: {
    ...getBaseProperties({ user, accessibleRoles }),
    crmUserId: { type: ['string', 'null'], title: 'CRM User ID' },
    crmLogin: { type: ['string', 'null'], title: 'CRM Login' },
    dealer_id: { type: 'string' },
    fniUserId: {
      type: ['string', 'null'],
      title: 'F&I User ID',
    },
  },
});

export const getEmployeeSchema = ({ user, currentUser, accessibleRoles }) => {
  const properties = getBaseProperties({ user, accessibleRoles });
  // remove role editing for ADMINS to avoid accidentally self-demotion
  if (
    user.roles &&
    (user.roles.includes(UserRole.ADMIN) ||
      user.roles.includes(UserRole.SUPER_ADMIN)) &&
    currentUser.id === user.id
  ) {
    delete properties.roles;
  }
  return {
    title: 'Upstart User Specifications',
    type: 'object',
    required: [...getUserRequiredFields({ user }), 'type'],
    properties: {
      ...properties,
      type: {
        type: 'string',
        title: 'Type',
        enum: [UserType.EMPLOYEE, UserType.ADVANCED],
      },
    },
  };
};

export const changePasswordSchema = {
  title: 'Change User Password',
  type: 'object',
  required: ['password'],
  properties: {
    email: { type: 'string', title: 'Email' },
    password: { type: 'string', title: 'Password' },
  },
};

export const getDealerAccessSchema = dealers => ({
  title: 'Dealership Access',
  type: 'object',
  required: ['id'],
  properties: {
    dealers: {
      type: 'array',
      title: 'Dealers',
      items: {
        type: 'object',
        properties: {
          id: {
            type: 'string',
            title: 'Dealer',
            enum: dealers.map(d => d.id),
            enumNames: dealers.map(d => startCase(d.name)),
          },
          crmUserId: {
            type: ['string', 'null'],
            title: 'CRM User Id',
            maxLength: 36,
          },
          fniUserId: {
            type: ['string', 'null'],
            title: 'F&I User ID',
          },
        },
      },
      uniqueItems: true,
    },
    id: { type: 'number' },
  },
});

export const uiSchema = {
  id: {
    'ui:readonly': true,
    classNames: 'user-editor-id-hidden',
  },
  phone: {
    'ui:field': 'phone',
    'ui:help': 'Format: xxx-xxx-xxxx',
  },
  password: {
    'ui:widget': 'password',
    'ui:placeholder': '**********',
  },
  roles: {
    'ui:widget': 'checkboxes',
    classNames: 'col-md-12',
  },
  dealers: {
    classNames: 'col-md-12',
    'ui:options': {
      orderable: false,
    },
    items: {
      id: { classNames: 'col-md-6' },
      crmUserId: { classNames: 'col-md-3' },
      fniUserId: { classNames: 'col-md-3' },
    },
  },
  dealer_id: { 'ui:widget': 'hidden' },
};

export const changePasswordUiSchema = {
  id: {
    'ui:readonly': true,
  },
  email: {
    'ui:readonly': true,
  },
  password: {
    'ui:widget': 'password',
    'ui:placeholder': '**********',
  },
};
